import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { FIELD_KEY } from "@/lib/offlineStore";

// Offline evidence queue only (geo-tagged photos/notes awaiting upload) — never tokens or credentials; auth lives in httpOnly cookies.
export const FIELD_QUEUE_KEY = FIELD_KEY;
const KEY = FIELD_KEY;
const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };
const write = (q) => localStorage.setItem(KEY, JSON.stringify(q));
const dataUrlToBlob = async (u) => (await fetch(u)).blob();
const removeItem = (client_id) => { const q = read().filter((i) => i.client_id !== client_id); write(q); return q; };

export async function syncFieldQueue(onChange) {
  let synced = 0, failed = 0;
  for (const item of read()) {
    const fd = new FormData();
    ["project_id", "client_id", "kind", "lat", "lng", "accuracy_m", "altitude_m", "heading", "note", "captured_at", "device"].forEach((k) => { if (item[k] !== undefined && item[k] !== null && item[k] !== "") fd.append(k, item[k]); });
    if (item.photo) fd.append("photo", await dataUrlToBlob(item.photo), item.photo_name || "capture.jpg");
    try {
      await api.post("/field/captures", fd, { headers: { "Content-Type": "multipart/form-data" } });
      synced += 1; const q = removeItem(item.client_id); onChange?.(q);
    } catch (e) {
      const status = e.response?.status;
      if (status && status >= 400 && status < 500 && status !== 401 && status !== 429) { const q = removeItem(item.client_id); onChange?.(q); failed += 1; }
      else { const q = read().map((i) => i.client_id === item.client_id ? { ...i, status: "retry", attempts: (i.attempts || 0) + 1, error: e.response?.data?.detail || e.message } : i); write(q); onChange?.(q); failed += 1; }
    }
  }
  return { synced, failed, pending: read().length };
}

export function useFieldQueue() {
  const [queue, setQueue] = useState(read);
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const enqueue = useCallback((item) => { const q = [...read(), { ...item, client_id: item.client_id || crypto.randomUUID(), status: "queued", attempts: 0 }]; write(q); setQueue(q); return q[q.length - 1]; }, []);
  const remove = useCallback((client_id) => setQueue(removeItem(client_id)), []);

  const sync = useCallback(async () => {
    if (!navigator.onLine || syncing) return { synced: 0, failed: 0 };
    setSyncing(true);
    const res = await syncFieldQueue(setQueue);
    setSyncing(false); setLastSync(new Date());
    return res;
  }, [syncing]);

  useEffect(() => {
    const up = () => { setOnline(true); sync(); };
    const down = () => setOnline(false);
    const external = () => setQueue(read());
    window.addEventListener("online", up); window.addEventListener("offline", down); window.addEventListener("qs-offline-changed", external);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); window.removeEventListener("qs-offline-changed", external); };
  }, [sync]);

  return { queue, online, syncing, lastSync, enqueue, remove, sync };
}
